/**
 * Internationalization (i18n) Configuration
 * Uses react-i18next for multilingual support
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import translation files
import jaCommon from '../locales/ja/common.json';
import jaAuth from '../locales/ja/auth.json';
import jaWatch from '../locales/ja/watch.json';
import jaAdmin from '../locales/ja/admin.json';
import jaStreamer from '../locales/ja/streamer.json';
import jaValidation from '../locales/ja/validation.json';
import jaFooter from '../locales/ja/footer.json';
import jaGuide from '../locales/ja/guide.json';

import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enWatch from '../locales/en/watch.json';
import enAdmin from '../locales/en/admin.json';
import enStreamer from '../locales/en/streamer.json';
import enValidation from '../locales/en/validation.json';
import enFooter from '../locales/en/footer.json';
import enGuide from '../locales/en/guide.json';

// Storage key for language preference
const LANG_STORAGE_KEY = 'lang';

/**
 * Detect user's preferred language
 * Priority: localStorage > browser language > default (ja)
 */
const detectLanguage = (): string => {
  // 1. Check localStorage for saved preference
  const saved = localStorage.getItem(LANG_STORAGE_KEY);
  if (saved && ['ja', 'en'].includes(saved)) {
    return saved;
  }

  // 2. Check browser language
  const browserLang = navigator.language.split('-')[0];
  if (browserLang === 'ja') {
    return 'ja';
  }

  // 3. Default to Japanese for now
  // Can be changed to 'en' for non-Japanese browsers later
  return 'ja';
};

/**
 * Save language preference to localStorage
 */
export const saveLanguagePreference = (lang: string): void => {
  localStorage.setItem(LANG_STORAGE_KEY, lang);
};

/**
 * Change language and save preference
 */
export const changeLanguage = async (lang: string): Promise<void> => {
  await i18n.changeLanguage(lang);
  saveLanguagePreference(lang);
};

/**
 * Get current language
 */
export const getCurrentLanguage = (): string => {
  return i18n.language;
};

/**
 * Get available languages
 */
export const getAvailableLanguages = (): { code: string; name: string; nativeName: string }[] => {
  return [
    { code: 'ja', name: 'Japanese', nativeName: '日本語' },
    { code: 'en', name: 'English', nativeName: 'English' },
  ];
};

// Initialize i18next
i18n.use(initReactI18next).init({
  resources: {
    ja: {
      common: jaCommon,
      auth: jaAuth,
      watch: jaWatch,
      admin: jaAdmin,
      streamer: jaStreamer,
      validation: jaValidation,
      footer: jaFooter,
      guide: jaGuide,
    },
    en: {
      common: enCommon,
      auth: enAuth,
      watch: enWatch,
      admin: enAdmin,
      streamer: enStreamer,
      validation: enValidation,
      footer: enFooter,
      guide: enGuide,
    },
  },
  lng: detectLanguage(),
  fallbackLng: 'ja',
  defaultNS: 'common',
  ns: ['common', 'auth', 'watch', 'admin', 'streamer', 'validation', 'footer', 'guide'],
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  react: {
    useSuspense: false, // Disable suspense for simpler error handling
  },
});

export default i18n;
